import { TrueImage } from "../TrueImage/TrueImage";
import { html, css } from "../TrueBase/TrueBase";

export class TrueImageResponsive extends TrueImage {
  static get properties() {
    return {
      focalPoint: {
        type: String,
        attribute: false,
      },
      maxWidth: {
        type: Number,
      },
      maxHeight: {
        type: Number,
      },
      focalpointLandscape: {
        type: String,
      },
      focalpointPortrait: {
        type: String,
      },
      focalpointSquare: {
        type: String,
      },
      focalpointWide: {
        type: String,
      },
      focalpointTall: {
        type: String,
      },
      focalpointDefault: {
        type: String,
      },
      quality: {
        type: Number,
      },
      resizeImage: {
        type: HTMLElement,
        attribute: false,
      },
      resizeObserver: {
        type: ResizeObserver,
        attribute: false,
      },
      frame: {
        type: Number,
        attribute: false,
      },
      timer: {
        type: Number,
        attribute: false,
      },
      webPImage: {
        type: HTMLElement,
        attribute: false,
      },
      cacheBusterValue: {
        type: String,
      },
      imageLoaded: {
        type: Boolean,
        reflect: false,
        attribute: false,
      },
      placeholderSrc: {
        type: String,
      },
      hasWebP: {
        type: Boolean,
      },
    };
  }

  constructor() {
    super();
    this.altText = "";
    this.maxWidth = 1920;
    this.maxHeight = 1080;
    this.imageLoaded = false;
    this.quality = 80;
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    this.resizeObserver = new ResizeObserver(
      this._handleSizeAvailable.bind(this)
    );
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver.unobserve(this);
    if (this.timer) {
      clearTimeout(this.timer);
    }
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
    }
  }

  firstUpdated() {
    this.resizeImage = this.shadowRoot.querySelector("#image");
    if (this.hasWebP) {
      this.webPImage = this.shadowRoot.querySelector("#webpImage");
    }
  }

  _handleSizeAvailable() {
    if (!this.imageLoaded) {
      this.imageLoaded = true;
      this._getImageParams();
    } else {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
      }
      this.timer = setTimeout(() => {
        this.frame = window.requestAnimationFrame(() => {
          this._getImageParams();
        });
      }, 2000);
    }
  }
  /**
   * Gets widh and height of image and call functions.
   * @constructor
   */
  _getImageParams() {
    this.width = this.resizeImage.clientWidth;
    this.height = this.resizeImage.clientHeight;
    this._checkForMaxSize();
    const ratio = this._getRatio(this.width, this.height);
    this._getOrientation(ratio);
    this._buildUrl();
  }

  _checkForMaxSize() {
    this.width =
      this.width <= this.maxWidth
        ? this.resizeImage.clientWidth
        : this.maxWidth;
    this.height =
      this.height <= this.maxHeight
        ? this.resizeImage.clientHeight
        : this.maxHeight;
  }

  /**
   * Calculates the ratio of the image.
   * @constructor
   * @param {number} x - The width of the image.
   * @param {number} y - The height of the image.
   */
  _getRatio(x, y) {
    const c = this._gcd(x, y);
    return x / c / (y / c);
  }

  /**
   * Finds the greatest common divisor between two integers
   * (https://stackoverflow.com/questions/1186414/whats-the-algorithm-to-calculate-aspect-ratio).
   * @constructor
   * @param {number} a - First number.
   * @param {number} b - Second number.
   */
  _gcd(a, b) {
    if (b > a) {
      const temp = a;
      a = b;
      b = temp;
    }
    while (b != 0) {
      const m = a % b;
      a = b;
      b = m;
    }
    return a;
  }

  /**
   * Gets the right focal point depending on the aspectratio.
   * @constructor
   * @param {number} aspectRatio - Ratio of the image.
   */
  _getOrientation(aspectRatio) {
    if (aspectRatio < 0.5) {
      this.focalPoint =
        this.focalpointTall != null
          ? this.focalpointTall
          : this.focalpointDefault;
    } else if (aspectRatio < 1) {
      this.focalPoint =
        this.focalpointPortrait != null
          ? this.focalpointPortrait
          : this.focalpointDefault;
    } else if (aspectRatio > 1 && aspectRatio < 1.5) {
      this.focalPoint =
        this.focalpointSquare != null
          ? this.focalpointSquare
          : this.focalpointDefault;
    } else if (aspectRatio > 1.5 && aspectRatio < 3) {
      this.focalPoint =
        this.focalpointLandscape != null
          ? this.focalpointLandscape
          : this.focalpointDefault;
    } else if (aspectRatio > 3) {
      this.focalPoint =
        this.focalpointWide != null
          ? this.focalpointWide
          : this.focalpointDefault;
    }
  }

  /**
   * Build the image src url.
   * @constructor
   */
  _buildUrl() {
    let params = [];
    if (this.focalPoint) {
      params.push(`crop=${this.focalPoint}`);
    }
    params.push(`cropmode=percentage`);
    if (this.width) {
      params.push(`width=${this.width}`);
    }
    if (this.height) {
      params.push(`height=${this.height}`);
    }
    params.push("mode=crop");
    if (this.focalpointDefault) {
      params.push(`center=${this.focalpointDefault}`);
    }
    if (this.quality) {
      params.push(`quality=${this.quality}`);
    }
    if (this.cacheBusterValue) {
      params.push(`rnd=${this.cacheBusterValue}`);
    }
    let newImageSrc = `${this.imageSrc}?${params.join("&")}`;
    this.resizeImage.src = newImageSrc;
    if (this.hasWebP) {
      this.webPImage.srcset = `${newImageSrc}&format=webp`;
    }
  }

  render() {
    return html`
      <picture>
        ${this.hasWebP
          ? html` <source id="webpImage" type="image/webp" /> `
          : ""}
        <img
          id="image"
          width="100%"
          height="100%"
          .loading="${this.lazyload}"
          .alt="${this.altText}"
          importance=${this.lazyload == "eager" ? "high" : "low"}
        />
      </picture>
      <slot></slot>
    `;
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          overflow: hidden;
          transition: opacity 0.3s ease-in-out;
        }
      `,
    ];
  }
}

window.customElements.get("true-image-responsive") ||
  window.customElements.define("true-image-responsive", TrueImageResponsive);
